import { useRef } from 'react'
import { UseFormReturn } from 'react-hook-form'

import { OperationVariables, TypedDocumentNode, useLazyQuery } from '@apollo/client'
import { useDebounceCallback } from 'usehooks-ts'

import { SchemaType } from 'modules/shift_management/lib/forms/GenerateCoverageForm'

interface DebounceQueryProps<TData, TVariables extends OperationVariables, K extends keyof TData> {
  query: TypedDocumentNode<TData, TVariables>
  buildVariables: (value: Date) => TVariables
  onResolve: (result: TData[K]) => void
  delay: number
  key: K
  form: UseFormReturn<SchemaType>
  fieldName: keyof SchemaType
}

const useDebounceQuery = <TData, TVariables extends OperationVariables, K extends keyof TData>({
  query,
  buildVariables,
  onResolve,
  delay,
  key,
  form,
  fieldName,
}: DebounceQueryProps<TData, TVariables, K>) => {
  const [runQuery] = useLazyQuery(query)
  const lastScheduleRef = useRef<Date | null>(null)

  const debouncedRunQuery = useDebounceCallback(async (value: Date) => {
    if (lastScheduleRef.current !== value) return
    form.setValue(fieldName, value)
    const result = await runQuery({
      variables: buildVariables(value),
    })
    if (result.data) {
      onResolve(result.data[key])
    }
  }, delay)

  const onChange = (value: Date) => {
    lastScheduleRef.current = value
    debouncedRunQuery(value)
  }

  return { onChange }
}

export { useDebounceQuery }
